import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { adminApi, type AdminAgentAvatarState } from '@/lib/api'
import { AvatarPicker, applyAvatarChoice, type AvatarChoice } from './AvatarPicker'
import { AlertCircle, Code, User, Settings, Brain, MessageSquare, Database, X } from 'lucide-react'

interface LLMConfig {
  model: string
  temperature?: number
  maxTokens?: number
}

interface LLMModel {
  id: string
  name: string
  provider: string
  description: string
}

interface Agent {
  id: string
  email: string
  name: string
  type?: string
  isActive?: boolean
  visibility?: string
  instanceSystemPrompt?: string
  llmConfig?: LLMConfig | null
  createdAt?: string
  updatedAt?: string
  ownerId?: string | null
  intentDetectionLLMConfig?: LLMConfig | null
  description?: string
  shortDescription?: string
  isDefault?: boolean
  availableAgents?: string[] | null
  welcomeMessage?: string | null
  specialties?: string[] | null
  interactionFrequency?: string
  collaborationStyle?: string | null
  correspondenceCount?: number
  stars?: number
  messagesSentCount?: number
  deletedAt?: string | null
  deletedById?: string | null
  deletedByType?: string | null
  knowledgeBaseIds?: string[] | null
  workflowIds?: string[] | null
  localWorkflowSlugs?: string[] | null
}

/** A bundled guided flow, as listed by GET /workflows/local/metadata */
interface GuidedFlowMeta {
  slug: string | null
  title: string | null
  filename: string
}

interface AgentEditDialogProps {
  agent: Agent | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: () => void
}

export function AgentEditDialog({ agent, open, onOpenChange, onSave }: AgentEditDialogProps) {
  const [loading, setLoading] = useState(false)
  const [loadingDetails, setLoadingDetails] = useState(false)
  const [formData, setFormData] = useState<Partial<Agent>>({})
  const [llmConfig, setLlmConfig] = useState<LLMConfig>({
    model: 'gpt-4',
    temperature: 0.7,
    maxTokens: 4096
  })
  const [intentLlmConfig, setIntentLlmConfig] = useState<LLMConfig>({
    model: 'gpt-3.5-turbo',
    temperature: 0.3,
    maxTokens: 1024
  })
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([])
  const [defaultModel, setDefaultModel] = useState<string>('')
  const [loadingModels, setLoadingModels] = useState(false)
  const [knowledgeBases, setKnowledgeBases] = useState<any[]>([])
  const [agentKBIds, setAgentKBIds] = useState<string[]>([])
  const [loadingKBs, setLoadingKBs] = useState(false)
  const [workflows, setWorkflows] = useState<any[]>([])
  const [agentWorkflowIds, setAgentWorkflowIds] = useState<string[]>([])
  const [loadingWorkflows, setLoadingWorkflows] = useState(false)
  const [guidedFlows, setGuidedFlows] = useState<GuidedFlowMeta[]>([])
  const [agentGuidedSlugs, setAgentGuidedSlugs] = useState<string[]>([])
  // Avatar: a binding to an Avatar artifact (docs/avatar-integration-spec.md §2.4)
  const [avatarState, setAvatarState] = useState<AdminAgentAvatarState | null>(null)
  const [avatarChoice, setAvatarChoice] = useState<AvatarChoice>(undefined)

  // Load full agent details and platform settings when dialog opens
  useEffect(() => {
    if (open && agent?.id) {
      setAvatarChoice(undefined)
      setAvatarState(null)
      adminApi.getAgentAvatar(agent.id).then(setAvatarState).catch((error) => {
        console.error('Failed to load agent avatar:', error)
      })
      loadAgentDetails(agent.id)
      loadLLMSettings()
      loadKnowledgeBases()
      loadAgentKnowledgeBases(agent.id)
      loadWorkflows()
      loadGuidedFlows()
    }
  }, [open, agent?.id])

  const loadLLMSettings = async () => {
    try {
      setLoadingModels(true)
      const llmSettings = await adminApi.getLLMModelSettings()
      setAvailableModels(llmSettings.availableModels || [])
      setDefaultModel(llmSettings.defaultModel || '')
    } catch (error) {
      console.error('Failed to load LLM settings:', error)
    } finally {
      setLoadingModels(false)
    }
  }

  const loadAgentDetails = async (agentId: string) => {
    try {
      setLoadingDetails(true)
      const details = await adminApi.getAgentDetails(agentId)
      setFormData(details)

      // Parse LLM configs
      if (details.llmConfig) {
        setLlmConfig(details.llmConfig)
      }
      if (details.intentDetectionLLMConfig) {
        setIntentLlmConfig(details.intentDetectionLLMConfig)
      }
      // Initialize workflow IDs
      setAgentWorkflowIds(details.workflowIds || [])
      setAgentGuidedSlugs(details.localWorkflowSlugs || [])
    } catch (error) {
      console.error('Failed to load agent details:', error)
    } finally {
      setLoadingDetails(false)
    }
  }

  const loadKnowledgeBases = async () => {
    try {
      setLoadingKBs(true)
      const response = await adminApi.getKnowledgeBases({ limit: 100 })
      setKnowledgeBases(response.knowledgeBases || response || [])
    } catch (error) {
      console.error('Failed to load knowledge bases:', error)
    } finally {
      setLoadingKBs(false)
    }
  }

  const loadAgentKnowledgeBases = async (agentId: string) => {
    try {
      const response = await adminApi.getAgentKnowledgeBases(agentId)
      setAgentKBIds(response.knowledgeBaseIds || [])
    } catch (error) {
      console.error('Failed to load agent knowledge bases:', error)
    }
  }

  const loadWorkflows = async () => {
    try {
      setLoadingWorkflows(true)
      const response = await adminApi.getWorkflows({ limit: 100 })
      // API returns { success: true, data: [...] } format
      const workflowsData = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
          ? response
          : []
      setWorkflows(workflowsData)
    } catch (error) {
      console.error('Failed to load workflows:', error)
      setWorkflows([]) // Set empty array on error
    } finally {
      setLoadingWorkflows(false)
    }
  }

  // Guided flows ship with the app; the backend lists the bundled definitions.
  const loadGuidedFlows = async () => {
    try {
      const response = await adminApi.getLocalWorkflowsMetadata()
      const rows: GuidedFlowMeta[] = Array.isArray(response?.data) ? response.data : []
      setGuidedFlows(rows.filter(r => !!r.slug))
    } catch (error) {
      console.error('Failed to load guided flows:', error)
      setGuidedFlows([])
    }
  }

  const toggleGuidedFlow = (slug: string) => {
    setAgentGuidedSlugs(prev => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug])
  }

  const toggleKnowledgeBase = async (kbId: string) => {
    if (!agent?.id) return

    try {
      if (agentKBIds.includes(kbId)) {
        await adminApi.removeAgentKnowledgeBase(agent.id, kbId)
        setAgentKBIds(prev => prev.filter(id => id !== kbId))
      } else {
        await adminApi.addAgentKnowledgeBase(agent.id, kbId)
        setAgentKBIds(prev => [...prev, kbId])
      }
    } catch (error) {
      console.error('Failed to update knowledge base association:', error)
    }
  }

  const toggleWorkflow = (workflowId: string) => {
    if (agentWorkflowIds.includes(workflowId)) {
      setAgentWorkflowIds(prev => prev.filter(id => id !== workflowId))
    } else {
      setAgentWorkflowIds(prev => [...prev, workflowId])
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!agent?.id) return

    try {
      setLoading(true)
      
      // Prepare the update data. The avatar is not a field here: it is the
      // binding written below.
      const fields = formData as Partial<Agent>
      const updateData = {
        ...fields,
        llmConfig: llmConfig,
        intentDetectionLLMConfig: intentLlmConfig,
        workflowIds: agentWorkflowIds,
        localWorkflowSlugs: agentGuidedSlugs,
        // Convert specialties from string to array if needed
        specialties: typeof formData.specialties === 'string'
          ? (formData.specialties as string).split(',').map((s: string) => s.trim()).filter(Boolean)
          : formData.specialties,
        // Convert availableAgents from string to array if needed
        availableAgents: typeof formData.availableAgents === 'string'
          ? (formData.availableAgents as string).split(',').map((s: string) => s.trim()).filter(Boolean)
          : formData.availableAgents
      }

      await adminApi.updateAgent(agent.id, updateData)
      await applyAvatarChoice(agent.id, avatarChoice)
      onSave()
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to update agent:', error)
    } finally {
      setLoading(false)
    }
  }

  if (!agent) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Agent: {agent.name}</DialogTitle>
          <DialogDescription>
            Configure all aspects of the agent including system prompts, LLM settings, and behavior.
          </DialogDescription>
        </DialogHeader>

        {loadingDetails ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="basic" className="w-full">
              <TabsList className="grid w-full grid-cols-7">
                <TabsTrigger value="basic">
                  <User className="h-4 w-4 mr-2" />
                  Basic
                </TabsTrigger>
                <TabsTrigger value="prompts">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Prompts
                </TabsTrigger>
                <TabsTrigger value="llm">
                  <Brain className="h-4 w-4 mr-2" />
                  LLM Config
                </TabsTrigger>
                <TabsTrigger value="knowledge">
                  <Database className="h-4 w-4 mr-2" />
                  Knowledge
                </TabsTrigger>
                <TabsTrigger value="workflows">
                  <Settings className="h-4 w-4 mr-2" />
                  Flows
                </TabsTrigger>
                <TabsTrigger value="behavior">
                  <Settings className="h-4 w-4 mr-2" />
                  Behavior
                </TabsTrigger>
                <TabsTrigger value="advanced">
                  <Code className="h-4 w-4 mr-2" />
                  Advanced
                </TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      id="name"
                      value={formData.name || ''}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shortDescription">Short Description</Label>
                  <Input
                    id="shortDescription"
                    value={formData.shortDescription || ''}
                    onChange={(e) => setFormData({...formData, shortDescription: e.target.value})}
                    placeholder="Brief one-line description"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Full Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of the agent's purpose and capabilities"
                    rows={4}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <AvatarPicker
                    current={avatarState?.avatar ?? null}
                    currentIsOwn={avatarState?.source === 'template' || avatarState?.source === 'agent'}
                    choice={avatarChoice}
                    onChoose={setAvatarChoice}
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={formData.type || 'Correspondent'}
                      onValueChange={(value) => setFormData({...formData, type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Correspondent">Correspondent</SelectItem>
                        <SelectItem value="Feedback">Feedback</SelectItem>
                        <SelectItem value="Research">Research</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="visibility">Visibility</Label>
                  <Select
                    value={formData.visibility || 'public'}
                    onValueChange={(value) => setFormData({...formData, visibility: value})}
                  >
                    <SelectTrigger id="visibility">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public - Visible to all users in UI</SelectItem>
                      <SelectItem value="private">Private - Only visible to the owner/user</SelectItem>
                      <SelectItem value="hidden">Hidden - Hidden from UI but usable via ID</SelectItem>
                      <SelectItem value="system">System - Only used internally (not shown or selectable)</SelectItem>
                      <SelectItem value="beta">Beta - Shown to early access/testers</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Controls whether this agent appears in the App client. Only "Public" agents are shown to all users.
                  </p>
                </div>

                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isActive !== false}
                      onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                    />
                    <span>Active</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isDefault || false}
                      onChange={(e) => setFormData({...formData, isDefault: e.target.checked})}
                    />
                    <span>Default Agent</span>
                  </label>
                </div>
              </TabsContent>

              <TabsContent value="prompts" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="instanceSystemPrompt">
                    System Prompt
                    <span className="text-xs text-muted-foreground ml-2">
                      This defines the agent's personality and behavior
                    </span>
                  </Label>
                  <Textarea
                    id="instanceSystemPrompt"
                    value={formData.instanceSystemPrompt || ''}
                    onChange={(e) => setFormData({...formData, instanceSystemPrompt: e.target.value})}
                    placeholder="You are a helpful assistant..."
                    rows={10}
                    className="font-mono text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="welcomeMessage">
                    Welcome Message
                    <span className="text-xs text-muted-foreground ml-2">
                      First message sent to users
                    </span>
                  </Label>
                  <Textarea
                    id="welcomeMessage"
                    value={formData.welcomeMessage || ''}
                    onChange={(e) => setFormData({...formData, welcomeMessage: e.target.value})}
                    placeholder="Hello! I'm here to help you with..."
                    rows={4}
                  />
                </div>
              </TabsContent>

              <TabsContent value="llm" className="space-y-6 mt-4">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <Brain className="h-5 w-5 mr-2" />
                    Main LLM Configuration
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="llm-model">
                        Model
                        {(!llmConfig.model || llmConfig.model === defaultModel) && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (Using platform default)
                          </span>
                        )}
                      </Label>
                      <Select 
                        value={llmConfig.model || 'default'}
                        onValueChange={(value) => {
                          const modelValue = value === 'default' ? '' : value
                          setLlmConfig({...llmConfig, model: modelValue})
                        }}
                      >
                        <SelectTrigger id="llm-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">
                            Use Platform Default ({availableModels.find(m => m.id === defaultModel)?.name || defaultModel})
                          </SelectItem>
                          {loadingModels ? (
                            <SelectItem value="loading" disabled>
                              Loading models...
                            </SelectItem>
                          ) : (
                            availableModels.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name} ({model.provider})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="llm-temperature">
                        Temperature: {llmConfig.temperature}
                      </Label>
                      <input
                        id="llm-temperature"
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={llmConfig.temperature}
                        onChange={(e) => setLlmConfig({...llmConfig, temperature: parseFloat(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="llm-max-tokens">Max Tokens</Label>
                    <Input
                      id="llm-max-tokens"
                      type="number"
                      value={llmConfig.maxTokens}
                      onChange={(e) => setLlmConfig({...llmConfig, maxTokens: parseInt(e.target.value)})}
                      min="1"
                      max="32768"
                    />
                  </div>
                </div>

                <div className="border-t pt-6 space-y-4">
                  <h3 className="text-lg font-semibold flex items-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Intent Detection LLM Configuration
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="intent-model">
                        Model
                        {(!intentLlmConfig.model || intentLlmConfig.model === defaultModel) && (
                          <span className="text-xs text-muted-foreground ml-2">
                            (Using platform default)
                          </span>
                        )}
                      </Label>
                      <Select 
                        value={intentLlmConfig.model || 'default'}
                        onValueChange={(value) => {
                          const modelValue = value === 'default' ? '' : value
                          setIntentLlmConfig({...intentLlmConfig, model: modelValue})
                        }}
                      >
                        <SelectTrigger id="intent-model">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">
                            Use Platform Default ({availableModels.find(m => m.id === defaultModel)?.name || defaultModel})
                          </SelectItem>
                          {loadingModels ? (
                            <SelectItem value="loading" disabled>
                              Loading models...
                            </SelectItem>
                          ) : (
                            availableModels.map((model) => (
                              <SelectItem key={model.id} value={model.id}>
                                {model.name} ({model.provider})
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="intent-temperature">
                        Temperature: {intentLlmConfig.temperature}
                      </Label>
                      <input
                        id="intent-temperature"
                        type="range"
                        min="0"
                        max="2"
                        step="0.1"
                        value={intentLlmConfig.temperature}
                        onChange={(e) => setIntentLlmConfig({...intentLlmConfig, temperature: parseFloat(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="intent-max-tokens">Max Tokens</Label>
                    <Input
                      id="intent-max-tokens"
                      type="number"
                      value={intentLlmConfig.maxTokens}
                      onChange={(e) => setIntentLlmConfig({...intentLlmConfig, maxTokens: parseInt(e.target.value)})}
                      min="1"
                      max="32768"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="knowledge" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center">
                      <Database className="h-5 w-5 mr-2" />
                      Knowledge Base Access
                    </h3>
                    <div className="text-sm text-muted-foreground">
                      {agentKBIds.length} of {knowledgeBases.length} selected
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground">
                    Select which Knowledge Bases this agent can access and use to answer questions.
                  </p>

                  {loadingKBs ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {knowledgeBases.length === 0 ? (
                        <div className="text-center py-8">
                          <Database className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium mb-2">No Knowledge Bases Found</h3>
                          <p className="text-muted-foreground">
                            Create some Knowledge Bases first to associate them with agents.
                          </p>
                        </div>
                      ) : (
                        knowledgeBases.map((kb) => {
                          const isSelected = agentKBIds.includes(kb.id)
                          return (
                            <div
                              key={kb.id}
                              className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                                isSelected 
                                  ? 'border-primary bg-primary/10' 
                                  : 'border-border bg-background hover:bg-muted'
                              }`}
                              onClick={() => toggleKnowledgeBase(kb.id)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  isSelected 
                                    ? 'border-primary bg-primary' 
                                    : 'border-muted-foreground'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                                <div>
                                  <h4 className="font-medium">{kb.name}</h4>
                                  {kb.description && (
                                    <p className="text-sm text-muted-foreground">{kb.description}</p>
                                  )}
                                  <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                                    <span>{kb.documentCount || 0} documents</span>
                                    <span>Status: {kb.status}</span>
                                    <span>Updated: {new Date(kb.updatedAt).toLocaleDateString()}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                {isSelected && (
                                  <div className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                                    Selected
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}

                  {agentKBIds.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-2">Selected Knowledge Bases ({agentKBIds.length}):</h4>
                      <div className="flex flex-wrap gap-2">
                        {agentKBIds.map((kbId) => {
                          const kb = knowledgeBases.find(k => k.id === kbId)
                          return kb ? (
                            <div
                              key={kbId}
                              className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded text-sm"
                            >
                              <span>{kb.name}</span>
                              <button
                                type="button"
                                onClick={() => toggleKnowledgeBase(kbId)}
                                className="hover:bg-primary/20 rounded p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : null
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="workflows" className="space-y-8 mt-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center">
                      <MessageSquare className="h-5 w-5 mr-2" />
                      Guided flows
                    </h3>
                    <div className="text-sm text-muted-foreground">
                      {agentGuidedSlugs.length} of {guidedFlows.length} selected
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Step-by-step question flows that ship with the app and end in a Plan. The ones selected appear under "Guided flows" on this agent's start page.
                  </p>

                  <div className="space-y-2">
                    {guidedFlows.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-4 text-center">
                        No bundled guided flows found. Build the artifacts package to export them.
                      </p>
                    ) : (
                      guidedFlows.map((flow) => {
                        const slug = flow.slug as string
                        const isSelected = agentGuidedSlugs.includes(slug)
                        return (
                          <div
                            key={slug}
                            role="checkbox"
                            aria-checked={isSelected}
                            tabIndex={0}
                            className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-primary bg-primary/10'
                                : 'border-border bg-background hover:bg-muted'
                            }`}
                            onClick={() => toggleGuidedFlow(slug)}
                            onKeyDown={(e) => {
                              if (e.key === ' ' || e.key === 'Enter') {
                                e.preventDefault()
                                toggleGuidedFlow(slug)
                              }
                            }}
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                isSelected ? 'border-primary bg-primary' : 'border-muted-foreground'
                              }`}>
                                {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
                              </div>
                              <div>
                                <h4 className="font-medium">{flow.title || slug}</h4>
                                <p className="text-xs text-muted-foreground font-mono">{slug}</p>
                              </div>
                            </div>
                          </div>
                        )
                      })
                    )}
                  </div>

                  {agentGuidedSlugs.some(s => !guidedFlows.find(f => f.slug === s)) && guidedFlows.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Also assigned, but not bundled in this build: {agentGuidedSlugs.filter(s => !guidedFlows.find(f => f.slug === s)).join(', ')}
                    </p>
                  )}
                </div>

                <div className="space-y-4 border-t pt-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold flex items-center">
                      <Settings className="h-5 w-5 mr-2" />
                      Workflows
                    </h3>
                    <div className="text-sm text-muted-foreground">
                      {agentWorkflowIds.length} of {workflows.length} selected
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Published backend Workflows (run by the workflow engine) this agent offers. The ones selected appear under "Workflows" on this agent's start page.
                  </p>

                  {loadingWorkflows ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {workflows.length === 0 ? (
                        <div className="text-center py-8">
                          <Settings className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <h3 className="text-lg font-medium mb-2">No Workflows Found</h3>
                          <p className="text-muted-foreground">
                            Create some workflows first to associate them with agents.
                          </p>
                        </div>
                      ) : (
                        workflows.map((workflow) => {
                          const isSelected = agentWorkflowIds.includes(workflow.id)
                          return (
                            <div
                              key={workflow.id}
                              className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                                isSelected
                                  ? 'border-primary bg-primary/10'
                                  : 'border-border bg-background hover:bg-muted'
                              }`}
                              onClick={() => toggleWorkflow(workflow.id)}
                            >
                              <div className="flex items-center space-x-3">
                                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                                  isSelected
                                    ? 'border-primary bg-primary'
                                    : 'border-muted-foreground'
                                }`}>
                                  {isSelected && (
                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                  )}
                                </div>
                                <div>
                                  <h4 className="font-medium">{workflow.title || workflow.name || workflow.slug}</h4>
                                  {workflow.description && (
                                    <p className="text-sm text-muted-foreground">{workflow.description}</p>
                                  )}
                                  <div className="flex items-center space-x-4 text-xs text-muted-foreground mt-1">
                                    <span>Version: {workflow.version || '1.0.0'}</span>
                                    {workflow.status && <span>Status: {workflow.status}</span>}
                                    {workflow.updatedAt && <span>Updated: {new Date(workflow.updatedAt).toLocaleDateString()}</span>}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                {isSelected && (
                                  <div className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded">
                                    Selected
                                  </div>
                                )}
                              </div>
                            </div>
                          )
                        })
                      )}
                    </div>
                  )}

                  {agentWorkflowIds.length > 0 && (
                    <div className="border-t pt-4">
                      <h4 className="text-sm font-medium mb-2">Selected Workflows ({agentWorkflowIds.length}):</h4>
                      <div className="flex flex-wrap gap-2">
                        {agentWorkflowIds.map((workflowId) => {
                          const workflow = workflows.find(w => w.id === workflowId)
                          return workflow ? (
                            <div
                              key={workflowId}
                              className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded text-sm"
                            >
                              <span>{workflow.title || workflow.name || workflow.slug}</span>
                              <button
                                type="button"
                                onClick={() => toggleWorkflow(workflowId)}
                                className="hover:bg-primary/20 rounded p-0.5"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : null
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="behavior" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="interactionFrequency">Interaction Frequency</Label>
                  <Select 
                    value={formData.interactionFrequency || 'on_demand'}
                    onValueChange={(value) => setFormData({...formData, interactionFrequency: value})}
                  >
                    <SelectTrigger id="interactionFrequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="on_demand">On Demand</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="collaborationStyle">Collaboration Style</Label>
                  <Input
                    id="collaborationStyle"
                    value={formData.collaborationStyle || ''}
                    onChange={(e) => setFormData({...formData, collaborationStyle: e.target.value})}
                    placeholder="e.g., Socratic, Direct, Supportive"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="specialties">
                    Specialties
                    <span className="text-xs text-muted-foreground ml-2">
                      Comma-separated list
                    </span>
                  </Label>
                  <Textarea
                    id="specialties"
                    value={Array.isArray(formData.specialties) ? formData.specialties.join(', ') : formData.specialties || ''}
                    onChange={(e) => setFormData({...formData, specialties: e.target.value as any})}
                    placeholder="writing, analysis, creative tasks, technical support"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="availableAgents">
                    Available Agents
                    <span className="text-xs text-muted-foreground ml-2">
                      Comma-separated list of agent IDs this agent can collaborate with
                    </span>
                  </Label>
                  <Textarea
                    id="availableAgents"
                    value={Array.isArray(formData.availableAgents) ? formData.availableAgents.join(', ') : formData.availableAgents || ''}
                    onChange={(e) => setFormData({...formData, availableAgents: e.target.value as any})}
                    placeholder="agent-id-1, agent-id-2"
                    rows={2}
                  />
                </div>
              </TabsContent>

              <TabsContent value="advanced" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Agent ID</Label>
                    <Input value={agent.id} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Owner ID</Label>
                    <Input 
                      value={formData.ownerId || ''} 
                      onChange={(e) => setFormData({...formData, ownerId: e.target.value})}
                      placeholder="User ID"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Created At</Label>
                    <Input value={formData.createdAt ? new Date(formData.createdAt).toLocaleString() : ''} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Updated At</Label>
                    <Input value={formData.updatedAt ? new Date(formData.updatedAt).toLocaleString() : ''} disabled />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Messages Sent</Label>
                    <Input value={formData.messagesSentCount || 0} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Correspondence Count</Label>
                    <Input value={formData.correspondenceCount || 0} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Stars</Label>
                    <Input value={formData.stars || 0} disabled />
                  </div>
                </div>

                {formData.deletedAt && (
                  <div className="border border-destructive/50 bg-destructive/10 rounded-lg p-4 space-y-2">
                    <h4 className="font-semibold text-destructive">Deletion Information</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs">Deleted At</Label>
                        <p>{new Date(formData.deletedAt).toLocaleString()}</p>
                      </div>
                      <div>
                        <Label className="text-xs">Deleted By ID</Label>
                        <p>{formData.deletedById || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs">Deleted By Type</Label>
                        <p>{formData.deletedByType || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default AgentEditDialog